import { messageFor, type ApiErrorBody } from '@foundation/shared';
import { readOrgLocation, type OrgConfig } from './orgContext';

export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly details?: unknown,
    serverMessage?: string,
  ) {
    // ใช้ข้อความจากเซิร์ฟเวอร์ก่อนเสมอ เพราะเขียนไว้บอกวิธีแก้จริง ๆ
    // messageFor(code) เป็นข้อความรวม ๆ ต่อ code ซึ่งมักไม่พอให้ผู้ใช้ทำอะไรต่อ
    super(serverMessage?.trim() || messageFor(code));
    this.name = 'ApiRequestError';
  }
}

/**
 * token เก็บในหน่วยความจำเท่านั้น ไม่ลง localStorage
 *
 * LIFF เปิดใหม่ทุกครั้งที่กดจากเมนู และ ID token ของ LINE ใช้ขอ token ใหม่ได้เสมอ
 * จึงไม่มีเหตุผลต้องเก็บ token ค้างไว้บนเครื่อง — ลดความเสี่ยงถ้ามือถือหาย
 */
let accessToken: string | null = null;

export const tokenStore = {
  get: () => accessToken,
  set: (t: string) => {
    accessToken = t;
  },
  clear: () => {
    accessToken = null;
  },
};

let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  idempotent?: boolean;
  signal?: AbortSignal;
  /** ข้ามการแนบ token (ใช้กับ /auth/liff และ /auth/link) */
  anonymous?: boolean;
}

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/**
 * บอกฝั่งเซิร์ฟเวอร์ว่าเป็นของมูลนิธิไหน — **เฉพาะเมื่อลิงก์ระบุมาจริง ๆ**
 *
 * ถ้าส่ง slug ที่เดาเอาเองไปด้วย จะทับการหาจากโดเมน: มูลนิธิที่ใช้โดเมนของ
 * ตัวเอง (ลิงก์ไม่มี slug) จะถูกมองเป็นมูลนิธิเริ่มต้นทันที ซึ่งฝั่งผู้บริจาค
 * แปลว่า QR พร้อมเพย์ชี้ไปบัญชีของอีกมูลนิธิ
 *
 * ไม่ส่ง header = ให้เซิร์ฟเวอร์หาเองจากโดเมน (ดู middleware/publicOrg.ts)
 * และถ้าหาไม่ได้จริง ๆ มันจะตอบ error ที่บอกวิธีแก้ ไม่ใช่เดาให้
 */
function orgHeader(): Record<string, string> {
  const loc = readOrgLocation();
  return loc.explicit ? { 'x-org': loc.slug } : {};
}

/** ข้อมูลมูลนิธิที่ต้องรู้ก่อน liff.init() — ยังไม่มี token ตอนเรียก */
export async function fetchOrgConfig(app: 'staff' | 'donor'): Promise<OrgConfig> {
  // ลิงก์ไม่มี slug (มูลนิธิใช้โดเมนของตัวเอง หรือระบบมีมูลนิธิเดียว)
  // → ไม่ใส่ slug ในเส้นทาง ให้เซิร์ฟเวอร์หาจากโดเมนที่เรียกเข้ามาแทน
  const loc = readOrgLocation();
  const path = loc.explicit
    ? `/public/org/${encodeURIComponent(loc.slug)}/liff-config`
    : '/public/org/liff-config';
  const res = await fetch(`${BASE}${path}?app=${app}`);
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = data as ApiErrorBody;
    throw new ApiRequestError(
      err?.error?.code ?? 'INTERNAL_ERROR',
      res.status,
      err?.error?.details,
      err?.error?.message,
    );
  }
  return data as OrgConfig;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json', ...orgHeader() };
  if (!opts.anonymous && accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (opts.idempotent) headers['idempotency-key'] = crypto.randomUUID();

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = data as ApiErrorBody;
    const code = err?.error?.code ?? 'INTERNAL_ERROR';
    if (code === 'UNAUTHENTICATED' || code === 'TOKEN_EXPIRED') {
      tokenStore.clear();
      onUnauthenticated?.();
    }
    throw new ApiRequestError(code, res.status, err?.error?.details, err?.error?.message);
  }

  return data as T;
}

export function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/**
 * เปิดใบอนุโมทนาบัตรของตัวเอง
 *
 * เส้นทางนี้ต้องใช้ Authorization header จึงเปิดด้วย <a href> ตรง ๆ ไม่ได้
 * ดึงมาเป็น blob แล้วเปิดแท็บใหม่แทน — ผู้บริจาคกดพิมพ์หรือบันทึกเป็น PDF ต่อได้
 */
export async function openReceipt(receiptId: string): Promise<void> {
  const token = tokenStore.get();
  const res = await fetch(`${BASE}/receipts/${receiptId}/print`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('เปิดใบอนุโมทนาบัตรไม่สำเร็จ');

  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank', 'noopener');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
